import PageRendererHelperBase from './PageRendererHelperBase';
import { GraphicItemUtils } from '../../../../utils/GraphicItemUtils';
import { GraphicItemMasks } from './GraphicItemMasks';
import { fabric } from 'fabric';
import FabricUtils from '../../../../utils/FabricUtils';
import emptyWellImage from '../../../../public/images/empty_well.png';

const EMPTY_PHOTO_WELL_ICON =
  'M36.489 16.0614C36.268 16.0614 36.0863 15.9878 35.944 15.8404C35.802 15.6928 35.731 15.5101 35.731 15.2924V12.0114H32.4885C32.2705 12.0114 32.0878 11.9368 31.9405 11.7874C31.7928 11.6378 31.719 11.4524 31.719 11.2314C31.719 11.0104 31.7928 10.8288 31.9405 10.6864C32.0878 10.5441 32.2705 10.4729 32.4885 10.4729H35.731V7.23093C35.731 7.01293 35.8056 6.83009 35.955 6.68243C36.1046 6.53509 36.29 6.46143 36.511 6.46143C36.732 6.46143 36.9136 6.53509 37.056 6.68243C37.198 6.83009 37.269 7.01293 37.269 7.23093V10.4729H40.5115C40.7295 10.4729 40.9121 10.5478 41.0595 10.6974C41.2071 10.8468 41.281 11.0319 41.281 11.2529C41.281 11.4739 41.2071 11.6556 41.0595 11.7979C40.9121 11.9403 40.7295 12.0114 40.5115 12.0114H37.269V15.2924C37.269 15.5101 37.1943 15.6928 37.045 15.8404C36.8953 15.9878 36.71 16.0614 36.489 16.0614ZM16.127 32.9384H31.654C31.9116 32.9384 32.1048 32.8179 32.2335 32.5769C32.3625 32.3359 32.3565 32.0949 32.2155 31.8539L28.0925 26.2924C27.9515 26.1181 27.7643 26.0309 27.531 26.0309C27.2976 26.0309 27.1095 26.1213 26.9665 26.3019L22.1425 32.3809L19.081 28.7114C18.9396 28.5628 18.7651 28.4884 18.5575 28.4884C18.3498 28.4884 18.1755 28.5756 18.0345 28.7499L15.6425 31.8539C15.4425 32.0949 15.4078 32.3359 15.5385 32.5769C15.6691 32.8179 15.8653 32.9384 16.127 32.9384ZM10.5 39.9999C9.75829 39.9999 9.11129 39.7238 8.55896 39.1714C8.00696 38.6194 7.73096 37.9726 7.73096 37.2309V10.7689C7.73096 10.0273 8.00696 9.38043 8.55896 8.82843C9.11129 8.27609 9.75829 7.99993 10.5 7.99993H27.6425C27.8601 7.99993 28.0428 8.07359 28.1905 8.22093C28.3378 8.36859 28.4115 8.55126 28.4115 8.76893V13.9344C28.4115 14.3268 28.5441 14.6556 28.8095 14.9209C29.0748 15.1863 29.4036 15.3189 29.796 15.3189H32.423V17.9844C32.423 18.3518 32.5556 18.6744 32.821 18.9524C33.0863 19.2301 33.4151 19.3689 33.8075 19.3689H38.9615C39.1795 19.3689 39.3621 19.4428 39.5095 19.5904C39.6571 19.7378 39.731 19.9204 39.731 20.1384V37.2309C39.731 37.9726 39.4548 38.6194 38.9025 39.1714C38.3501 39.7238 37.7031 39.9999 36.9615 39.9999H10.5Z';
const EMPTY_PHOTO_WELL_ICON_WIDTH = 48;
let emptyWellImageHTML;
let highlightImageHTML;

export default class PageRendererEmptyWellHelper extends PageRendererHelperBase {
  static getHighlightImage() {
    return highlightImageHTML;
  }
  static getEmptyWellImage() {
    return emptyWellImageHTML;
  }

  loadAddImageIconIntoCanvas(width) {
    let canvas = document.createElement('canvas');
    let context = canvas.getContext('2d');
    canvas.width = width * 0.2;
    canvas.height = width * 0.2;

    const p = new Path2D(EMPTY_PHOTO_WELL_ICON);

    const scaleFactor = canvas.height / EMPTY_PHOTO_WELL_ICON_WIDTH;

    context.fillStyle = '#737373';
    context.setTransform(scaleFactor, 0, 0, scaleFactor, 0, 0);
    context.fill(p);

    return canvas;
  }

  drawEmptyWell(pi, wellWidth, wellHeight, x, y) {
    return new Promise((resolve) => {
      this.wellWidth = wellWidth;
      this.wellHeight = wellHeight;
      this.x = x;
      this.y = y;

      this.emptyIcon = this.loadAddImageIconIntoCanvas(wellWidth, wellHeight);
      this.populateFilters(pi);

      let existingItem = this.getExistingCanvasObject(pi);

      if (
        !pi.get('imageID') &&
        existingItem &&
        existingItem.pageItemVO.get('imageID')
      ) {
        //this was a well with an image, so we need to add the placeholder back in
        this._canvas.remove(existingItem);
        existingItem = undefined;
      }

      if (existingItem != undefined) {
        if (this.isObjectUnchanged(pi, existingItem)) {
          resolve({ group: existingItem });
          return;
        }
        if (
          !this.isShapeUnchanged(pi, existingItem) ||
          !this.areDimensionsUnchanged(pi, existingItem)
        ) {
          let groupItems = this.decompileGroup(existingItem);

          this.loadImages(pi, true).then((response) => {
            this.loadedImages = response.images;

            existingItem.remove(groupItems.img);
            existingItem.remove(groupItems.border);

            let newImage = this.getFabricImage(pi);
            let newBorder = this.getBorderRect(pi);

            existingItem.insertAt(newBorder, 0);
            existingItem.insertAt(newImage, 1);
            existingItem.pageItemVO = pi;

            this.updateObjectProperties(existingItem, pi).then((group) => {
              if (group.group) {
                resolve(group);
              } else {
                resolve({ group: group });
              }
            });
          });

          return;
        }

        this.updateObjectProperties(existingItem, pi).then((group) => {
          if (group.group) {
            resolve(group);
          } else {
            resolve({ group: group });
          }
        });
      } else {
        // this.getEmptyWellGroup(pi).then((group)=>{
        //     resolve(group); //group already wrapped in an object here
        // });

        this.addNewObject(pi).then((group) => {
          resolve(group); //group already wrapped in an object here
        });
      }
    });
  }

  getEmptyWellShape(image, width, height) {
    let groupProps = {
      width: width,
      height: height,
      picabooType: 'emptyWellShape',
    };

    var shape = new fabric.Rect(groupProps);

    shape.set(
      'fill',
      new fabric.Pattern({
        source: image,
        repeat: 'repeat',
      }),
    );

    return shape;
  }

  loadImagePromise(img) {
    if (emptyWellImageHTML && img === emptyWellImage) {
      return Promise.resolve(emptyWellImageHTML);
    }

    return new Promise((resolve) => {
      FabricUtils.loadImage(
        img,
        function (htmlImage) {
          if (img === emptyWellImage) {
            emptyWellImageHTML = htmlImage;
            highlightImageHTML = this.createHighlightImage(htmlImage);
          }
          resolve(htmlImage);
        },
        this,
        'Anonymous',
      );
    });
  }

  createHighlightImage(htmlImage) {
    let canvas = document.createElement('canvas');
    let context = canvas.getContext('2d');
    canvas.width = htmlImage.width;
    canvas.height = htmlImage.height;
    context.drawImage(htmlImage, 0, 0);
    let imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    this.applyBrightness(imageData.data, 20);
    context.putImageData(imageData, 0, 0);
    return canvas;
  }

  applyBrightness(data, brightness) {
    for (var i = 0; i < data.length; i += 4) {
      data[i] += 255 * (brightness / 100);
      data[i + 1] += 255 * (brightness / 100);
      data[i + 2] += 255 * (brightness / 100);
    }
  }

  loadImages(pi) {
    return new Promise((resolve) => {
      let loadPromise = this.loadImagePromise(emptyWellImage);

      loadPromise.then((htmlImage) => {
        const imageCanvas = htmlImage;

        let images = {
          image: imageCanvas,
          fromCache: false,
        };

        let imagesToLoad = [];

        GraphicItemUtils.loadImages(imagesToLoad, [], function (loadedImages) {
          resolve({ images, pi });
        });
      });
    });
  }

  getFabricImage(pi) {
    let img = this.getEmptyWellShape(
      this.loadedImages.image,
      this.wellWidth,
      this.wellHeight,
    );
    let imageOpacity = pi.get('alpha') || 1;

    img.pageItemVO = pi;
    img.originX = 'center';
    img.originY = 'center';

    img.width = this.wellWidth;
    img.height = this.wellHeight;
    img.picabooType = 'emptyWellImage';
    img.opacity = imageOpacity;

    let maskShape = pi.get('maskShape') == 'null' ? null : pi.get('maskShape');

    if (maskShape) {
      img.clipTo = (ctx) => {
        GraphicItemMasks.applyMask(
          ctx,
          maskShape,
          this.wellWidth,
          this.wellHeight,
        );
      };
    } else {
      img.clipTo = null;
    }

    return img;
  }

  getEmptyWellText() {
    return this.isMobile ? 'Tap to insert photo' : 'Drag image here';
  }

  getDropText() {
    const min = Math.min(this.wellWidth, this.wellHeight);
    let image = new fabric.Image(this.emptyIcon, {
      originX: 'center',
      originY: 'center',
      picabooType: 'EmptyWellIcon',
    });

    let emptyWellText = new fabric.Text(this.getEmptyWellText(), {
      originX: 'center',
      originY: 'center',
      fill: '#737373',
      fontSize: Math.max(min / 24, 12),
      fontFamily: 'Proxima-Nova',
      top: image.height / 2 + 10,
      picabooType: 'EmptyWellText',
    });

    return new fabric.Group([image, emptyWellText], {
      originX: 'center',
      originY: 'center',
      picabooType: 'EmptyWellGroup',
    });
  }

  compileGroup(image, borderRect) {
    let text = this.getDropText();
    let ret = [borderRect, image, text];
    return ret;
  }

  decompileGroup(group) {
    const elements = group.getObjects();
    switch (elements.length) {
      case 4:
        return {
          border: elements[0],
          img: elements[1],
          droptext: elements[2],
          corners: elements[3],
        };
        break;
      case 3:
        return { border: elements[0], img: elements[1], droptext: elements[2] };
        break;
      case 2:
        return { border: elements[0], img: elements[1] };
        break;
    }
  }

  resizeImage(pi, image) {
    //don't need to do this here, so just return what's passed in
    return image;
  }
}
